import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react'
import { DashboardLoading } from 'components/atoms/DashboardLoading'
import { DashboardNotFound } from 'components/atoms/DashboardNotFound'
import { CustomBarChart } from 'components/molecules/CustomBarChart'
import { CustomPieChart } from 'components/molecules/CustomPieChart'
import { useInstitutionsContext } from 'contexts/institutions'
import { memo, useEffect, useMemo, useState } from 'react'
import { getDataDashboardBilling } from 'services/billing'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { useDebounce } from 'utils/useDebounce'
import {
    BillingQueryDTO,
    DashboardBillingResponseDTO,
    FormFilter
} from '../interface'

interface PropsModeDashboard {
    filter: FormFilter
    setLoadingFilter: (loading: boolean) => void
    idDashboard: string
}

const CustomTooltip = ({ active, payload, chartType }: any) => {
    if (!(active && payload && payload.length)) return null

    const data = payload[0].payload
    let label = ''
    let value = 0
    let formatter_fn: any = formatter.moeda

    switch (chartType) {
        case 'service':
            label = 'Valor Total Serviço'
            value = data.valorRegistradora
            break
        case 'tax':
            label = 'Valor Total Taxa'
            value = data.taxaRegistro
            break
        case 'quantity':
            label = 'Quantidade'
            value = data.quantidade
            formatter_fn = formatter.formatWithLeadingZero
            break
        default:
            return null
    }

    return (
        <Box
            bg="white"
            border="1px solid #ddd"
            p={2}
            borderRadius="md"
            shadow="sm"
        >
            <Text fontWeight="bold">{data.nome}</Text>
            <Box display="flex" gap="4px" justifyContent="space-between">
                <Text>{label}: </Text>
                <Text fontWeight="bold">{formatter_fn(value)}</Text>
            </Box>
        </Box>
    )
}

const ModeDashboard: React.FC<PropsModeDashboard> = memo(
    ({ filter, idDashboard, setLoadingFilter }) => {
        const [dashboardData, setDashboardData] =
            useState<DashboardBillingResponseDTO>({ dados: [] })
        const [loading, setLoading] = useState<boolean>(true)
        const { currentInstitution, currentAgent } = useInstitutionsContext()

        const buildPayload = (): Partial<BillingQueryDTO> => {
            return removeEmptyValues({
                cnpjAgente: currentAgent?.cnpj ?? '',
                instituicaoFinanceira: currentInstitution?.id ?? '',
                ...filter,
                periodoInicial: filter?.periodoInicial
                    ? formatter.formatDateToPayload(filter?.periodoInicial)
                    : '',
                periodoFinal: filter?.periodoFinal
                    ? formatter.formatDateToPayload(filter?.periodoFinal)
                    : ''
            })
        }

        const fetchDashboard = async () => {
            try {
                setLoading(true)
                setLoadingFilter(true)
                const { data } = await getDataDashboardBilling(buildPayload())
                if (data) {
                    setDashboardData(data)
                } else {
                    console.warn('Formato inesperado da resposta:', data)
                    setDashboardData({ dados: [] })
                }
            } catch (error) {
                console.error('Erro ao buscar dados do dashboard:', error)
                setDashboardData({ dados: [] })
            } finally {
                setLoading(false)
                setLoadingFilter(false)
            }
        }

        const bounceAPI = useDebounce(fetchDashboard, 1000)

        useEffect(() => {
            bounceAPI()
        }, [filter, currentAgent?.cnpj, currentInstitution?.id])

        const DashboardSections = useMemo(() => {
            if (!dashboardData?.dados?.length) return null

            const sectionConfigs = [
                ...dashboardData.dados.map((grupo, index) => ({
                    title:
                        grupo.nome ||
                        (index === 0 ? 'Detran' : 'Agente Financeiro'),
                    dataIndex: index,
                    charts: [
                        {
                            title: 'Serviços  (R$)',
                            type: 'bar',
                            dataKey: 'valorRegistradora',
                            chartType: 'service'
                        },
                        {
                            title: 'Taxa  (R$)',
                            type: 'bar',
                            dataKey: 'taxaRegistro',
                            chartType: 'tax'
                        },
                        {
                            title: 'Quantidade',
                            type: 'pie',
                            dataKey: 'quantidade',
                            chartType: 'quantity'
                        }
                    ]
                }))
            ]

            return sectionConfigs.flatMap(section => {
                // Verifica se temos os dados para este índice
                const sectionData = dashboardData?.dados[section.dataIndex]
                if (!sectionData?.values || !Array.isArray(sectionData.values))
                    return []

                return section.charts.map((chart, idx) => (
                    <GridItem key={`${section.title}-${chart.title}-${idx}`}>
                        <Box
                            bg="white"
                            borderRadius="md"
                            shadow="lg"
                            p={6}
                            transition="all 0.3s"
                            _hover={{ shadow: 'xl' }}
                            height="100%"
                        >
                            <Heading size="md" marginY={8} textAlign="center">
                                {section.title} {chart.title}
                            </Heading>

                            {chart.type === 'bar' ? (
                                <CustomBarChart
                                    children={(props: any) => (
                                        <CustomTooltip
                                            {...props}
                                            chartType={chart.chartType}
                                        />
                                    )}
                                    dataKeyBar={chart.dataKey}
                                    dataKeyYAxis="nome"
                                    values={
                                        dashboardData.dados[section.dataIndex]
                                            .values
                                    }
                                />
                            ) : (
                                <CustomPieChart
                                    dataKey={chart.dataKey}
                                    outerRadius={150}
                                    values={
                                        dashboardData.dados[section.dataIndex]
                                            .values
                                    }
                                    children={(props: any) => (
                                        <CustomTooltip
                                            {...props}
                                            chartType={chart.chartType}
                                        />
                                    )}
                                />
                            )}
                        </Box>
                    </GridItem>
                ))
            })
        }, [dashboardData])

        if (loading) {
            return <DashboardLoading />
        }

        return (
            <div id={idDashboard}>
                {dashboardData?.dados?.length > 0 ? (
                    <Grid
                        templateColumns="repeat(auto-fit, minmax(600px, 1fr))"
                        gap={6}
                    >
                        {DashboardSections}
                    </Grid>
                ) : (
                    <DashboardNotFound />
                )}
            </div>
        )
    }
)

export default ModeDashboard
